import { i18n } from "@/i18n";
import { accessManager } from "@/services/security/access-manager";
import { adminManager } from "@/services/admin/admin-manager";
import TemplateEditView from "@/views/Admin/template/TemplateEditView.vue";
import TemplateListView from "@/views/Admin/template/TemplateListView.vue";
import GlobalLayoutView from "@/views/Global/GlobalLayoutView.vue";

export default {
  path: "/admin",
  name: "adminLayout",
  redirect: { name: "adminHome" },
  component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/LayoutView.vue"),
  meta: {
    authRequired: true,
    required_role: "ROLE_STUDEA_ADMIN",
    title: i18n.global.t("admin.home.title"),
    breadcrumb: true,
  },
  children: [
    {
      path: "config",
      name: "adminHome",
      component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/AdminHomeView.vue"),
      meta: {
        title: i18n.global.t("admin.home.title2"),
        breadcrumb: false,
      },
    },
    {
      path: "users",
      name: "adminUsers",
      redirect: { name: "adminUsersList" },
      component: () => import(/* webpackChunkName: "global" */ "@/views/Global/GlobalLayoutView.vue"),
      meta: {
        title: i18n.global.tc("global.list.title", 1, {
          name: i18n.global.tc("admin.user.title", 2).toLowerCase(),
        }),
        breadcrumb: true,
        code: adminManager.STUDEA_ADMIN_USER,
        access: accessManager.VIEW,
      },
      children: [
        {
          path: "",
          name: "adminUsersList",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/user/UsersListView.vue"),
          meta: {
            title: i18n.global.tc("global.list.title", 1, {
              name: i18n.global.tc("admin.user.title", 2).toLowerCase(),
            }),
            breadcrumb: false,
            code: adminManager.STUDEA_ADMIN_USER,
            access: accessManager.VIEW,
          },
        },
        {
          path: "add",
          name: "adminUsersAdd",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/user/UserAddEditView.vue"),
          meta: {
            title: i18n.global.tc("global.add.title", 1, {
              name: i18n.global.tc("admin.user.title", 1).toLowerCase(),
            }),
            backPath: "adminUsers",
            breadcrumb: true,
            code: adminManager.STUDEA_ADMIN_USER,
            access: accessManager.EDIT,
          },
        },
        {
          path: "edit/:id",
          name: "adminUsersEdit",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/user/UserAddEditView.vue"),
          meta: {
            title: i18n.global.tc("global.edit.title", 1, {
              name: i18n.global.tc("admin.user.title", 1).toLowerCase(),
            }),
            backPath: "adminUsers",
            breadcrumb: true,
            code: adminManager.STUDEA_ADMIN_USER,
            access: accessManager.EDIT,
          },
        },
        {
          path: "studea-manager-profile",
          name: "adminStudeaManagerProfileList",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/user/UsersProfileListView.vue"),
          meta: {
            title: i18n.global.tc("global.list.title", 1, {
              name: i18n.global.tc("admin.studeaManagerProfile.title", 2).toLowerCase(),
            }),
            backPath: "adminUsers",
            breadcrumb: true,
            code: adminManager.STUDEA_ADMIN_ROLE,
            access: accessManager.VIEW,
          },
        },
        {
          path: ":alias(studea-manager-profile)/add",
          name: "adminStudeaManagerProfileAdd",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/AddEditView.vue"),
          meta: {
            title: i18n.global.tc("global.add.title", 1, {
              name: i18n.global.tc("admin.studeaManagerProfile.title", 1).toLowerCase(),
            }),
            breadcrumb: true,
            notGeneric: true,
            backPath: "adminStudeaManagerProfileList",
            code: adminManager.STUDEA_ADMIN_ROLE,
            access: accessManager.EDIT,
          },
        },
        {
          path: ":alias(studea-manager-profile)/edit/:id",
          name: "adminStudeaManagerProfileEdit",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/AddEditView.vue"),
          meta: {
            title: i18n.global.tc("global.edit.title", 1, {
              name: i18n.global.tc("admin.studeaManagerProfile.title", 1).toLowerCase(),
            }),
            breadcrumb: true,
            notGeneric: true,
            backPath: "adminStudeaManagerProfileList",
            code: adminManager.STUDEA_ADMIN_ROLE,
            access: accessManager.EDIT,
          },
        },
        {
          path: "profile-title-list",
          name: "UsersProfileTitleList",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/user/UsersProfileTitleListView.vue"),
          meta: {
            title: i18n.global.t("admin.user.profile_title"),
            breadcrumb: true,
            code: adminManager.STUDEA_ADMIN_USER,
            access: accessManager.EDIT,
          },
        },
        {
          path: "profile-title-edit/:id",
          name: "UserProfileEdit",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/user/UsersProfileEditView.vue"),
          meta: {
            title: i18n.global.t("admin.user.modify_profile"),
            breadcrumb: true,
            code: adminManager.STUDEA_ADMIN_USER,
            access: accessManager.EDIT,
          },
        },
        {
          path: ":alias(user)/import-form",
          name: "UsersImport",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/ImportView.vue"),
          meta: {
            title: i18n.global.t("admin.user.import_users"),
            breadcrumb: true,
            code: adminManager.STUDEA_ADMIN_USER,
            access: accessManager.EDIT,
          },
        },
        {
          path: "merge",
          name: "UserMerge",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/user/UserMergeView.vue"),
          meta: {
            title: i18n.global.t("admin.user.merge"),
            breadcrumb: true,
          },
        },
      ],
    },
    {
      path: "template/:templateType",
      name: "adminTemplate",
      redirect: (to) => ({
        name: "adminTemplateList",
        params: { templateType: to.params.templateType },
      }),
      component: GlobalLayoutView,
      props: true,
      meta: {
        title: i18n.global.tc("global.list.title", 2, {
          name: i18n.global.tc("admin.modele.title", 2).toLowerCase(),
        }),
        editPath: "adminTemplateEdit",
        breadcrumb: true,
        code: adminManager.STUDEA_ADMIN_TEMPLATE,
        access: accessManager.VIEW,
      },
      children: [
        {
          path: "list",
          name: "adminTemplateList",
          component: TemplateListView,
          props: true,
          meta: {
            title: i18n.global.tc("global.list.title", 2, {
              name: i18n.global.tc("admin.modele.title", 2).toLowerCase(),
            }),
            breadcrumb: false,
            code: adminManager.STUDEA_ADMIN_TEMPLATE,
            access: accessManager.VIEW,
          },
        },
        {
          path: "edit/:id",
          name: "adminTemplateEdit",
          component: TemplateEditView,
          props: true,
          meta: {
            title: i18n.global.tc("global.list.title", 2, {
              name: i18n.global.tc("admin.modele.title", 2).toLowerCase(),
            }),
            breadcrumb: false,
            notGeneric: true,
            backPath: "adminTemplateList",
            code: adminManager.STUDEA_ADMIN_TEMPLATE,
            access: accessManager.VIEW,
          },
        },
      ],
    },
    {
      path: "trainings",
      name: "adminTrainings",
      redirect: { name: "adminTrainingsList" },
      component: () => import(/* webpackChunkName: "global" */ "@/views/Global/GlobalLayoutView.vue"),
      meta: {
        title: i18n.global.tc("global.list.title", 2, {
          name: i18n.global.tc("admin.training.title", 2).toLowerCase(),
        }),
        breadcrumb: true,
        code: adminManager.STUDEA_ADMIN_TRAINING,
        access: accessManager.VIEW,
      },
      children: [
        {
          path: "",
          name: "adminTrainingsList",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/training/TrainingsListView.vue"),
          meta: {
            title: i18n.global.tc("global.list.title", 2, {
              name: i18n.global.tc("admin.training.title", 2).toLowerCase(),
            }),
            breadcrumb: false,
            code: adminManager.STUDEA_ADMIN_TRAINING,
            access: accessManager.VIEW,
          },
        },
        {
          path: ":alias(training)/add",
          name: "adminTrainingsAdd",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/AddEditView.vue"),
          meta: {
            breadcrumb: true,
            title: i18n.global.tc("global.add.title", 2, {
              name: i18n.global.tc("admin.training.title", 1).toLowerCase(),
            }),
            notGeneric: true,
            backPath: "adminTrainingsList",
            code: adminManager.STUDEA_ADMIN_TRAINING,
            access: accessManager.EDIT,
          },
        },
        {
          path: ":alias(training)/edit/:id",
          name: "adminTrainingsEdit",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/AddEditView.vue"),
          meta: {
            breadcrumb: true,
            title: i18n.global.tc("global.edit.title", 2, {
              name: i18n.global.tc("admin.training.title", 1).toLowerCase(),
            }),
            notGeneric: true,
            backPath: "adminTrainingsList",
            code: adminManager.STUDEA_ADMIN_TRAINING,
            access: accessManager.EDIT,
          },
        },
        {
          path: ":alias(training)/import",
          name: "adminTrainingsImport",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/ImportView.vue"),
          meta: {
            breadcrumb: true,
            title: i18n.global.tc("global.import.title", 2, {
              name: i18n.global.tc("admin.training.title", 1).toLowerCase(),
            }),
            backPath: "adminTrainingsList",
            code: adminManager.STUDEA_ADMIN_TRAINING,
            access: accessManager.EDIT,
          },
        },
        {
          path: ":alias(training)/archiving",
          name: "adminTrainingsArchiving",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/training/TrainingsArchivingView.vue"),
          meta: {
            breadcrumb: true,
            title: i18n.global.t("admin.archiving.title"),
            backPath: "adminTrainingsList",
            code: adminManager.STUDEA_ADMIN_TRAINING,
            access: accessManager.EDIT,
          },
        },
        {
          path: ":alias(training)/current-training-course-year",
          name: "adminTrainingsCurrentTrainingCourseYear",
          component: () =>
            import(/* webpackChunkName: "admin" */ "@/views/Admin/training/TrainingsEditCurrentYearView.vue"),
          meta: {
            breadcrumb: true,
            title: i18n.global.t("admin.current_training_course_year.title"),
            backPath: "adminTrainingsList",
            code: adminManager.STUDEA_ADMIN_TRAINING,
            access: accessManager.EDIT,
          },
        },
        {
          path: "training-course/edit/:id",
          name: "adminTrainingCourseEdit",
          component: () =>
            import(/* webpackChunkName: "admin" */ "@/views/Admin/trainingCourse/TrainingCourseEditView.vue"),
          meta: {
            title: i18n.global.tc("global.edit.title", 2, {
              name: i18n.global.tc("admin.trainingCourse.title", 1).toLowerCase(),
            }),
            breadcrumb: true,
            backPath: "adminTrainingsList",
            code: adminManager.STUDEA_ADMIN_TRAINING,
            access: accessManager.EDIT,
          },
        },
        {
          path: ":alias(triad)/import-triad",
          name: "adminTrainingImportTriad",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/ImportView.vue"),
          meta: {
            title: i18n.global.t("admin.training.import_triad"),
            breadcrumb: true,
            code: adminManager.STUDEA_ADMIN_TRAINING,
            access: accessManager.EDIT,
          },
        },
      ],
    },
    {
      path: "institutions",
      name: "adminInstitutions",
      component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/institution/InstitutionsListView.vue"),
      meta: {
        title: i18n.global.tc("global.list.title", 1, {
          name: i18n.global.tc("admin.institution.title", 2).toLowerCase(),
        }),
        breadcrumb: true,
        code: adminManager.STUDEA_ADMIN_INSTITUTION,
        access: accessManager.VIEW,
      },
    },
    {
      path: ":alias(help-document)",
      name: "adminHelpDocument",
      redirect: { name: "adminHelpDocumentSettings" },
      meta: {
        title: i18n.global.t("admin.help_settings"),
        breadcrumb: true,
        generic: "admin",
        code: adminManager.STUDEA_ADMIN_HELP,
        access: accessManager.VIEW,
      },
      children: [
        {
          path: "settings",
          name: "adminHelpDocumentSettings",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/design/DesignEditView.vue"),
          meta: {
            title: i18n.global.t("admin.help_settings"),
            breadcrumb: true,
            code: adminManager.STUDEA_ADMIN_HELP,
            access: accessManager.EDIT,
          },
        },
      ],
    },
    {
      path: "modules",
      name: "adminModule",
      redirect: { name: "adminModuleList" },
      component: () => import(/* webpackChunkName: "global" */ "@/views/Global/GlobalLayoutView.vue"),
      meta: {
        title: i18n.global.tc("global.list.title", 1, {
          name: i18n.global.tc("admin.modulet.title", 2).toLowerCase(),
        }),
        breadcrumb: true,
        code: adminManager.STUDEA_ADMIN_MODULE,
        access: accessManager.VIEW,
      },
      children: [
        {
          path: "",
          name: "adminModuleList",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/module/ModulesListView.vue"),
          meta: {
            title: i18n.global.tc("global.list.title", 1, {
              name: i18n.global.tc("admin.modulet.title", 2).toLowerCase(),
            }),
            breadcrumb: false,
            code: adminManager.STUDEA_ADMIN_MODULE,
            access: accessManager.VIEW,
          },
        },
        {
          path: "training-course/:id",
          name: "adminModuleTrainingCourses",
          component: () =>
            import(/* webpackChunkName: "admin" */ "@/views/Admin/module/ModulesTrainingCoursesView.vue"),
          meta: {
            title: i18n.global.t("admin.modulet.trainingCourse.edit.title"),
            breadcrumb: true,
            code: adminManager.STUDEA_ADMIN_MODULE,
            access: accessManager.EDIT,
          },
        },
        {
          path: ":alias",
          name: "adminModuleQuestionnaireConfig",
          component: () =>
            import(/* webpackChunkName: "admin" */ "@/views/Admin/module/ModuleQuestionnaireConfigView.vue"),
          meta: {
            title: i18n.global.t("admin.modulet.config.title"),
            breadcrumb: true,
            unique: true,
            withCancel: true,
            backPath: "adminModuleList",
            code: adminManager.STUDEA_ADMIN_MODULE,
            access: accessManager.ALL,
          },
        },
        {
          path: ":alias(ypareo_config)",
          name: "adminModuleYpareoConfig",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/module/YpareoConfigView.vue"),
          meta: {
            title: i18n.global.t("admin.modulet.config.title"),
            breadcrumb: true,
            unique: true,
            withCancel: true,
            backPath: "adminModuleList",
            code: adminManager.STUDEA_ADMIN_MODULE,
            access: accessManager.EDIT,
          },
        },
        {
          path: ":alias(ammon_config)",
          name: "adminModuleAmmonConfig",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/Ammon/AmmonConfigView.vue"),
          meta: {
            title: i18n.global.t("admin.modulet.config.title"),
            breadcrumb: true,
            unique: true,
            withCancel: true,
            backPath: "adminModuleList",
            code: adminManager.STUDEA_ADMIN_MODULE,
            access: accessManager.EDIT,
          },
        },
        {
          path: ":alias(driver-licence-config)",
          name: "adminModuleDriverLicenceConfig",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/AddEditView.vue"),
          meta: {
            title: i18n.global.t("admin.modulet.config.title"),
            breadcrumb: true,
            unique: true,
            withCancel: true,
            backPath: "adminModuleList",
            code: adminManager.STUDEA_ADMIN_MODULE,
            access: accessManager.EDIT,
          },
        },
        {
          path: ":alias(edusign_config)",
          name: "adminModuleEdusignConfig",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/AddEditView.vue"),
          meta: {
            title: i18n.global.t("admin.modulet.config.title"),
            breadcrumb: true,
            unique: true,
            withCancel: true,
            backPath: "adminModuleList",
            code: adminManager.STUDEA_ADMIN_MODULE,
            access: accessManager.EDIT,
          },
        },
        {
          path: ":alias(uniservice_config)",
          name: "adminModuleUniserviceConfig",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/uniservice/UniserviceConfigView.vue"),
          meta: {
            title: i18n.global.t("admin.modulet.config.title"),
            breadcrumb: true,
            unique: true,
            withCancel: true,
            backPath: "adminModuleList",
            code: adminManager.STUDEA_ADMIN_MODULE,
            access: accessManager.EDIT,
          },
        },
        {
          path: ":alias(gradebook_config)",
          name: "adminGradebookConfig",
          component: () =>
            import(/* webpackChunkName: "admin" */ "@/views/Admin/gradebook/AdminGradebookConfigView.vue"),
          meta: {
            title: i18n.global.t("admin.modulet.config.title"),
            breadcrumb: true,
            unique: true,
            withCancel: true,
            backPath: "adminModuleList",
            code: adminManager.STUDEA_ADMIN_MODULE,
            access: accessManager.EDIT,
          },
        },
        {
          path: ":alias(contract-rupture-config)",
          name: "adminModuleDeclareRuptureConfig",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/AddEditView.vue"),
          meta: {
            title: i18n.global.t("admin.modulet.config.title"),
            breadcrumb: true,
            unique: true,
            withCancel: true,
            backPath: "adminModuleList",
            code: adminManager.STUDEA_ADMIN_MODULE,
            access: accessManager.EDIT,
          },
        },
        {
          path: "absence-config",
          name: "adminModuleAbsenceConfig",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/module/ModuleAbsenceConfigView.vue"),
          meta: {
            title: i18n.global.t("admin.modulet.config.title"),
            breadcrumb: true,
            code: adminManager.STUDEA_ADMIN_MODULE,
            access: accessManager.EDIT,
          },
        },
        {
          path: "skill/configuration",
          name: "adminModuleSkillConfiguration",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/skill/SkillAddEditView.vue"),
          meta: {
            title: i18n.global.t("admin.modulet.skill_referential.config"),
            breadcrumb: true,
            code: adminManager.STUDEA_ADMIN_MODULE,
            access: accessManager.EDIT,
            backPath: "adminModuleList",
          },
        },
        {
          path: "skill/referential",
          name: "adminModuleSkillReferentialLayout",
          redirect: { name: "adminModuleSkillReferential" },
          component: () => import(/* webpackChunkName: "global" */ "@/views/Global/GlobalLayoutView.vue"),
          meta: {
            title: i18n.global.t("admin.modulet.skill_referential.list"),
            breadcrumb: true,
            code: adminManager.STUDEA_ADMIN_MODULE,
            access: accessManager.EDIT,
          },
          children: [
            {
              path: "list",
              name: "adminModuleSkillReferential",
              component: () =>
                import(/* webpackChunkName: "admin" */ "@/views/Admin/skill/SkillReferencialListView.vue"),
              meta: {
                title: i18n.global.t("admin.modulet.skill_referential.list"),
                breadcrumb: false,
                code: adminManager.STUDEA_ADMIN_MODULE,
                access: accessManager.EDIT,
              },
            },
            {
              path: "edit/:id",
              name: "adminModuleSkillReferentialEdition",
              component: () =>
                import(/* webpackChunkName: "admin" */ "@/views/Admin/skill/SkillReferencialEditView.vue"),
              meta: {
                title: i18n.global.t("admin.modulet.skill_referential.edit"),
                breadcrumb: true,
                code: adminManager.STUDEA_ADMIN_MODULE,
                backPath: "adminModuleSkillReferential",
                access: accessManager.EDIT,
              },
            },
            {
              path: ":alias(skill-referential)/add",
              name: "adminSkillReferentialAdd",
              component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/AddEditView.vue"),
              meta: {
                title: i18n.global.tc("global.add.title", 1, {
                  name: i18n.global.tc("admin.skillReferential.title", 1).toLowerCase(),
                }),
                breadcrumb: true,
                backPath: "adminModuleSkillReferential",
                code: adminManager.STUDEA_ADMIN_MODULE,
                access: accessManager.EDIT,
              },
            },
            {
              path: ":alias(skill-referential)/edit/:id",
              name: "adminSkillReferentialEdit",
              component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/AddEditView.vue"),
              meta: {
                title: i18n.global.tc("global.edit.title", 1, {
                  name: i18n.global.tc("studea_manager.skillReferential.title", 1).toLowerCase(),
                }),
                breadcrumb: true,
                backPath: "adminModuleSkillReferentialEdition",
                code: adminManager.STUDEA_ADMIN_MODULE,
                access: accessManager.EDIT,
              },
            },
            {
              path: ":skillReferentialId/:alias(skill-category)/add",
              name: "adminSkillCategoryAdd",
              component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/AddEditView.vue"),
              meta: {
                title: i18n.global.tc("global.add.title", 2, {
                  name: i18n.global.tc("studea_manager.skillCategory.title", 1).toLowerCase(),
                }),
                breadcrumb: true,
                backPath: "adminModuleSkillReferentialEdition",
                code: adminManager.STUDEA_ADMIN_MODULE,
                access: accessManager.EDIT,
              },
            },
            {
              path: ":skillReferentialId/:alias(skill-category)/edit/:id",
              name: "adminSkillCategoryEdit",
              component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/AddEditView.vue"),
              meta: {
                title: i18n.global.tc("global.edit.title", 2, {
                  name: i18n.global.tc("studea_manager.skillCategory.title", 1).toLowerCase(),
                }),
                breadcrumb: true,
                backPath: "adminModuleSkillReferentialEdition",
                code: adminManager.STUDEA_ADMIN_MODULE,
                access: accessManager.EDIT,
              },
            },
            {
              path: ":skillReferentialId/:alias(skill)/add/:id",
              name: "adminSkillAdd",
              component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/AddEditView.vue"),
              meta: {
                title: i18n.global.tc("global.add.title", 2, {
                  name: i18n.global.tc("studea_manager.skill.title", 1).toLowerCase(),
                }),
                breadcrumb: true,
                backPath: "adminModuleSkillReferentialEdition",
                code: adminManager.STUDEA_ADMIN_MODULE,
                access: accessManager.EDIT,
              },
            },
            {
              path: ":skillReferentialId/:alias(skill)/edit/:id",
              name: "adminSkillEdit",
              component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/AddEditView.vue"),
              meta: {
                title: i18n.global.tc("global.edit.title", 2, {
                  name: i18n.global.tc("studea_manager.skill.title", 1).toLowerCase(),
                }),
                breadcrumb: true,
                backPath: "adminModuleSkillReferentialEdition",
                code: adminManager.STUDEA_ADMIN_MODULE,
                access: accessManager.EDIT,
              },
            },
            {
              path: ":skillReferentialId/:alias(skill)/import",
              name: "adminSkillImport",
              component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/ImportView.vue"),
              meta: {
                title: i18n.global.t("studea_manager.skill_import.title"),
                breadcrumb: true,
                backPath: "adminModuleSkillReferential",
                code: adminManager.STUDEA_ADMIN_MODULE,
                access: accessManager.EDIT,
              },
            },
          ],
        },
        {
          path: "registration",
          name: "adminModuleRegistrationConfig",
          component: () => import(/* webpackChunkName: "global" */ "@/views/Global/GlobalLayoutView.vue"),
          redirect: { name: "adminModuleRegistrationDashboard" },
          meta: {
            title: i18n.global.t("admin.modulet.registration.title"),
            breadcrumb: true,
            code: adminManager.STUDEA_ADMIN_MODULE,
            access: accessManager.EDIT,
          },
          children: [
            {
              path: "dashboard",
              name: "adminModuleRegistrationDashboard",
              component: () =>
                import(/* webpackChunkName: "global" */ "@/views/Admin/registration/RegistrationDashboardView.vue"),
              meta: {
                title: i18n.global.t("admin.modulet.registration.title"),
                breadcrumb: false,
              },
            },
            {
              path: "pre-registration/edit/:id",
              name: "adminModuleRegistrationSheetEdit",
              component: () =>
                import(/* webpackChunkName: "global" */ "@/views/Admin/registration/PreRegistrationEditView.vue"),
              meta: {
                title: i18n.global.t("page.pre_registration_edit"),
                breadcrumb: true,
              },
            },
            {
              path: ":alias(cerfa-param)/edit/:id",
              name: "adminModuleCerfaParamEdit",
              component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/AddEditView.vue"),
              meta: {
                title: i18n.global.tc("global.edit.title", 1, {
                  name: i18n.global.tc("admin.cerfaParam.title", 1).toLowerCase(),
                }),
                backPath: "adminModuleRegistrationDashboard",
                breadcrumb: true,
              },
            },
            {
              path: ":alias(cerfa-param)/edit",
              name: "adminModuleCerfaParamEdit",
              component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/AddEditView.vue"),
              meta: {
                title: i18n.global.tc("global.edit.title", 1, {
                  name: i18n.global.tc("admin.cerfaParam.title", 1).toLowerCase(),
                }),
                backPath: "adminModuleRegistrationDashboard",
                breadcrumb: true,
              },
            },
          ],
        },

        {
          path: "skill/generate-sesssion",
          name: "adminModuleSkillGenerateSession",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/skill/SkillSessionListView.vue"),
          meta: {
            title: i18n.global.t("admin.modulet.skill_session.title"),
            breadcrumb: true,
            code: adminManager.STUDEA_ADMIN_MODULE,
            access: accessManager.EDIT,
          },
        },
        {
          path: ":alias(absence-reason)/add",
          name: "adminAbsenceReasonAdd",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/AddEditView.vue"),
          meta: {
            title: i18n.global.tc("global.add.title", 1, {
              name: i18n.global.tc("admin.absenceReason.title", 1).toLowerCase(),
            }),
            backPath: "adminModuleAbsenceConfig",
            breadcrumb: true,
            code: adminManager.STUDEA_ADMIN_MODULE,
            access: accessManager.EDIT,
          },
        },
        {
          path: ":alias(absence-reason)/edit/:id",
          name: "adminAbsenceReasonEdit",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/AddEditView.vue"),
          meta: {
            title: i18n.global.tc("global.edit.title", 1, {
              name: i18n.global.tc("admin.absenceReason.title", 1).toLowerCase(),
            }),
            backPath: "adminModuleAbsenceConfig",
            breadcrumb: true,
            code: adminManager.STUDEA_ADMIN_MODULE,
            access: accessManager.EDIT,
          },
        },
      ],
    },
    {
      path: ":alias/list",
      name: "adminList",
      component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/ListView.vue"),
      meta: {
        title: "Liste",
        breadcrumb: true,
        generic: "admin",
        access: accessManager.VIEW,
      },
    },
    {
      path: ":alias/add",
      name: "adminAdd",
      component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/AddEditView.vue"),
      meta: {
        title: "Ajouter",
        breadcrumb: true,
        generic: "admin",
        access: accessManager.EDIT,
      },
    },
    {
      path: ":alias/edit/:id",
      name: "adminEdit",
      component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/AddEditView.vue"),
      meta: {
        title: "Modifier",
        breadcrumb: true,
        generic: "admin",
        access: accessManager.EDIT,
      },
    },
    {
      path: ":alias(news)",
      name: "adminNews",
      redirect: { name: "adminModuleNewsList" },
      component: "",
      meta: {
        title: "Liste",
        access: accessManager.VIEW,
      },
      children: [
        {
          path: "list",
          name: "adminModuleNewsList",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/ListView.vue"),
          meta: {
            title: i18n.global.t("admin.news.news_list"),
            breadcrumb: true,
            access: accessManager.VIEW,
            addPath: "adminNewsAdd",
            editPath: "adminNewsEdit",
          },
        },
        {
          path: "add",
          name: "adminNewsAdd",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/news/NewsAddEditView.vue"),
          meta: {
            title: "Ajouter",
            breadcrumb: true,
            generic: "admin",
            access: accessManager.EDIT,
          },
        },
        {
          path: "edit/:id",
          name: "adminNewsEdit",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/news/NewsAddEditView.vue"),
          meta: {
            title: "Modifier",
            breadcrumb: true,
            generic: "admin",
            access: accessManager.EDIT,
          },
        },
      ],
    },
    {
      path: "design/edit",
      name: "adminDesign",
      component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/design/DesignEditView.vue"),
      meta: {
        title: i18n.global.tc("global.edit.title", 1, {
          name: i18n.global.tc("admin.design.title", 1).toLowerCase(),
        }),
        breadcrumb: true,
        code: adminManager.STUDEA_ADMIN_DESIGN,
        access: accessManager.EDIT,
      },
    },
    {
      path: ":alias(data-agreement)/edit",
      name: "adminDataAgreement",
      component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/dataAgreement/DataAgreementEditView.vue"),
      meta: {
        title: i18n.global.tc("global.edit.title", 0, {
          name: i18n.global.tc("admin.dataAgreement.title", 1).toLowerCase(),
        }),
        breadcrumb: true,
        code: adminManager.STUDEA_ADMIN_DATA_AGREEMENT,
        access: accessManager.EDIT,
      },
    },
    {
      path: "history",
      name: "adminHistory",
      redirect: { name: "adminLogsList" },
      meta: {
        title: i18n.global.tc("global.list.title", 0, {
          name: i18n.global.tc("admin.log.title", 2).toLowerCase(),
        }),
        code: adminManager.STUDEA_ADMIN_HISTORY,
        access: accessManager.ALL,
        breadcrumb: true,
      },
      children: [
        {
          path: "list",
          name: "adminLogsList",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/log/LogsListView.vue"),
          meta: {
            title: i18n.global.tc("global.list.title", 0, {
              name: i18n.global.tc("admin.log.title", 2).toLowerCase(),
            }),
            code: adminManager.STUDEA_ADMIN_HISTORY,
            access: accessManager.ALL,
          },
        },
        {
          path: "view/:id",
          name: "adminLogView",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/log/LogViewView.vue"),
          meta: {
            title: i18n.global.tc("global.view.title", 0, {
              name: i18n.global.tc("admin.log.title", 1).toLowerCase(),
            }),
            breadcrumb: true,
            code: adminManager.STUDEA_ADMIN_HISTORY,
            access: accessManager.VIEW,
          },
        },
      ],
    },
    {
      path: ":alias(ypareo-reporting)",
      name: "adminYpareoReporting",
      redirect: { name: "adminYpareoReportingList" },
      meta: {
        title: i18n.global.tc("global.list.title", 0, {
          name: i18n.global.tc("admin.ypareoReporting.title", 1).toLowerCase(),
        }),
        code: adminManager.STUDEA_ADMIN_YPAREO_REPORTING,
        access: accessManager.ALL,
        breadcrumb: true,
      },
      children: [
        {
          path: "list",
          name: "adminYpareoReportingList",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/reporting/ReportingListView.vue"),
          meta: {
            title: i18n.global.tc("global.list.title", 0, {
              name: i18n.global.tc("admin.ypareoReporting.title", 1).toLowerCase(),
            }),
            code: adminManager.STUDEA_ADMIN_YPAREO_REPORTING,
            access: accessManager.ALL,
          },
        },
        {
          path: "view/:id",
          name: "adminYpareoReportingView",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/reporting/ReportingView.vue"),
          meta: {
            title: i18n.global.tc("global.view.title", 0, {
              name: i18n.global.tc("admin.ypareoReporting.title", 1).toLowerCase(),
            }),
            breadcrumb: true,
            code: adminManager.STUDEA_ADMIN_YPAREO_REPORTING,
            access: accessManager.VIEW,
          },
        },
      ],
    },
    {
      path: ":alias(edusgin-reporting)",
      name: "adminEdusignReporting",
      redirect: { name: "adminEdusignReportingList" },
      meta: {
        title: i18n.global.tc("global.list.title", 0, {
          name: i18n.global.tc("admin.edusignReporting.title", 2).toLowerCase(),
        }),
        code: adminManager.STUDEA_ADMIN_EDUSIGN_REPORTING,
        access: accessManager.ALL,
        breadcrumb: true,
      },
      children: [
        {
          path: "list",
          name: "adminEdusignReportingList",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/reporting/ReportingListView.vue"),
          meta: {
            title: i18n.global.tc("global.list.title", 0, {
              name: i18n.global.tc("admin.edusignReporting.title", 2).toLowerCase(),
            }),
            code: adminManager.STUDEA_ADMIN_EDUSIGN_REPORTING,
            access: accessManager.ALL,
          },
        },
        {
          path: "view/:id",
          name: "adminEdusignReportingView",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/reporting/ReportingView.vue"),
          meta: {
            title: i18n.global.tc("global.view.title", 0, {
              name: i18n.global.tc("admin.edusignReporting.title", 1).toLowerCase(),
            }),
            breadcrumb: true,
            code: adminManager.STUDEA_ADMIN_EDUSIGN_REPORTING,
            access: accessManager.VIEW,
          },
        },
      ],
    },
    {
      path: ":alias(ammon-reporting)",
      name: "adminAmmonReporting",
      redirect: { name: "adminAmmonReportingList" },
      meta: {
        title: i18n.global.tc("global.list.title", 0, {
          name: i18n.global.tc("admin.ammonReporting.title", 2).toLowerCase(),
        }),
        code: adminManager.STUDEA_ADMIN_AMMON_REPORTING,
        access: accessManager.ALL,
        breadcrumb: true,
      },
      children: [
        {
          path: "list",
          name: "adminAmmonReportingList",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/reporting/ReportingListView.vue"),
          meta: {
            title: i18n.global.tc("global.list.title", 0, {
              name: i18n.global.tc("admin.ammonReporting.title", 2).toLowerCase(),
            }),
            code: adminManager.STUDEA_ADMIN_AMMON_REPORTING,
            access: accessManager.ALL,
          },
        },
        {
          path: "view/:id",
          name: "adminAmmonReportingView",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/reporting/ReportingView.vue"),
          meta: {
            title: i18n.global.tc("global.view.title", 0, {
              name: i18n.global.tc("admin.ammonReporting.title", 1).toLowerCase(),
            }),
            breadcrumb: true,
            code: adminManager.STUDEA_ADMIN_AMMON_REPORTING,
            access: accessManager.VIEW,
          },
        },
      ],
    },
    {
      path: "mail",
      name: "adminMails",
      redirect: { name: "adminMailsList" },
      meta: {
        title: i18n.global.tc("global.list.title", 0, {
          name: i18n.global.tc("admin.mail.title", 2).toLowerCase(),
        }),
        code: adminManager.STUDEA_ADMIN_MAIL,
        access: accessManager.ALL,
        breadcrumb: true,
      },
      children: [
        {
          path: "list",
          name: "adminMailsList",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/mail/MailsListView.vue"),
          meta: {
            title: i18n.global.tc("global.list.title", 0, {
              name: i18n.global.tc("admin.mail.title", 2).toLowerCase(),
            }),
            code: adminManager.STUDEA_ADMIN_MAIL,
            access: accessManager.ALL,
          },
        },
        {
          path: "view/:id",
          name: "adminMailView",
          component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/mail/MailViewView.vue"),
          meta: {
            title: i18n.global.tc("global.view.title", 0, {
              name: i18n.global.tc("admin.mail.title", 1).toLowerCase(),
            }),
            breadcrumb: true,
            code: adminManager.STUDEA_ADMIN_MAIL,
            access: accessManager.VIEW,
          },
        },
      ],
    },
    {
      path: "statistic",
      name: "adminStatistic",
      component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/remote/StatisticView.vue"),
      meta: {
        title: i18n.global.t("admin.statistic.title"),
        breadcrumb: true,
        code: adminManager.STUDEA_ADMIN_STATISTIC,
        access: accessManager.ALL,
      },
    },
    {
      path: "export",
      name: "adminExport",
      component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/remote/ExportView.vue"),
      meta: {
        title: i18n.global.t("admin.export.title"),
        breadcrumb: true,
        code: adminManager.STUDEA_ADMIN_EXPORT,
        access: accessManager.ALL,
      },
    },
    {
      path: "duplicate",
      name: "adminDuplicate",
      component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/remote/DuplicateView.vue"),
      meta: {
        title: i18n.global.t("admin.duplicate.title"),
        breadcrumb: true,
        code: adminManager.STUDEA_ADMIN_DUPLICATE,
        access: accessManager.ALL,
      },
    },
    {
      path: "archive",
      name: "adminArchive",
      component: () => import(/* webpackChunkName: "admin" */ "@/views/Admin/remote/ArchiveView.vue"),
      meta: {
        title: i18n.global.t("admin.archive.title"),
        breadcrumb: true,
        code: adminManager.STUDEA_ADMIN_ARCHIVE,
        access: accessManager.ALL,
      },
    },
    {
      path: "admin-news",
      name: "adminNewsLayout",
      redirect: { name: "adminNewsList" },
      component: () => import(/* webpackChunkName: "global" */ "@/views/Global/GlobalLayoutView.vue"),
      meta: {
        title: i18n.global.t("admin.news.list.title"),
        breadcrumb: true,
      },
      children: [
        {
          path: "",
          name: "adminNewsList",
          component: () => import(/* webpackChunkName: "news" */ "@/views/News/NewsListView.vue"),
          meta: {
            title: i18n.global.t("admin.news.list.title"),
            breadcrumb: false,
          },
        },
        {
          path: ":id",
          name: "adminNewsShow",
          component: () => import(/* webpackChunkName: "news" */ "@/views/News/NewsView.vue"),
          meta: {
            title: i18n.global.t("admin.news.list.title"),
            breadcrumb: true,
          },
        },
      ],
    },
  ],
};
